import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiCalendar,
  FiCheckCircle,
  FiLink,
  FiImage,
  FiPackage,
  FiPlus,
  FiRefreshCw,
  FiTrash2,
  FiUser,
  FiXCircle,
} from "react-icons/fi";
import MenuLateral from "../../Components/MenuLateral";
import api from "../../services/api";
import Swal from "sweetalert2";
import "./index.css";

const STATUS_LABELS = {
  PEDIDO_ORCADO: "Pedido orçado",
  PEDIDO_REALIZADO: "Pedido realizado",
  VENDA_CONCLUIDA: "Venda concluída",
  PEDIDO_CANCELADO: "Pedido cancelado",
};

const NEXT_STATUS_ACTIONS = {
  PEDIDO_ORCADO: [
    { status: "PEDIDO_REALIZADO", label: "Marcar como pedido realizado" },
    { status: "VENDA_CONCLUIDA", label: "Marcar venda como concluída", requiresApprovedSelections: true },
    { status: "PEDIDO_CANCELADO", label: "Cancelar pedido", danger: true },
  ],
  PEDIDO_REALIZADO: [
    { status: "VENDA_CONCLUIDA", label: "Marcar venda como concluída", requiresApprovedSelections: true },
    { status: "PEDIDO_CANCELADO", label: "Cancelar pedido", danger: true },
  ],
  VENDA_CONCLUIDA: [],
  PEDIDO_CANCELADO: [],
};

const PUBLICATION_STATUS_LABELS = {
  UNPUBLISHED: "Indisponível",
  PUBLISHED: "Publicado",
};

const SELECTION_STATUS_LABELS = {
  AWAITING_SELECTION: "Aguardando seleção",
  ADJUSTMENT_REQUESTED: "Ajuste solicitado",
  SELECTION_SENT: "Seleção enviada",
  SELECTION_APPROVED: "Seleção aprovada",
};

const PRODUCT_CATEGORY_LABELS = {
  album: "Álbum",
  digital_album: "Álbum digital",
  impact_album: "Álbum de impacto",
  link: "Link",
  single_photo: "Foto avulsa",
  other: "Outros",
};

function getErrorMessage(error) {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.join("<br/>");
  if (typeof message === "string") return message;
  return error?.message || "Erro ao comunicar com a API";
}

function formatDate(value) {
  return value ? new Date(value).toLocaleDateString() : "-";
}

function formatPublicationStatus(status) {
  return PUBLICATION_STATUS_LABELS[status] || status || "Estado não informado";
}

function formatProductCategory(category) {
  return PRODUCT_CATEGORY_LABELS[category] || category || "Categoria não informada";
}

function getCommercialStatusTone(status) {
  if (status === "PEDIDO_REALIZADO") return "ready";
  if (status === "VENDA_CONCLUIDA") return "completed";
  if (status === "PEDIDO_CANCELADO") return "cancelled";
  return "quoted";
}

function getSelectionStatusTone(status) {
  if (["SELECTION_SENT", "SELECTION_APPROVED"].includes(status)) return "ready";
  if (status === "ADJUSTMENT_REQUESTED") return "attention";
  return "neutral";
}

function getPublicationStatusTone(status) {
  return status === "PUBLISHED" ? "ready" : "neutral";
}

function getEventName(link, availableEvents) {
  return (
    link.eventName ||
    availableEvents.find((event) => event.id === link.eventId)?.name ||
    `Evento ${link.eventId || "não identificado"}`
  );
}

function isEventLinkEditable(order, item) {
  if (!order || !item) return false;

  if (!["PEDIDO_ORCADO", "PEDIDO_REALIZADO"].includes(order.commercialStatus)) {
    return false;
  }

  return !["SELECTION_SENT", "SELECTION_APPROVED"].includes(item.selectionStatus);
}

function isUnlinkImpactConflict(error) {
  return (
    error?.response?.status === 409 &&
    error?.response?.data?.code === "EVENT_UNLINK_REQUIRES_CONFIRMATION"
  );
}

function getEventList(response) {
  const data = response?.data;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.events)) return data.events;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

function VendaDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [eventDrafts, setEventDrafts] = useState({});
  const [eventAction, setEventAction] = useState("");
  const [loadingEventCatalog, setLoadingEventCatalog] = useState(false);
  const [eventCatalogError, setEventCatalogError] = useState("");

  const loadOrder = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/sales/${id}`);
      const nextOrder = response.data.order;
      const itemEventResponses = await Promise.all(
        (nextOrder?.items || []).map(async (item) => {
          try {
            const itemEventsResponse = await api.get(
              `/sales/${id}/items/${item.id}/events`,
            );

            return {
              itemId: item.id,
              events: itemEventsResponse.data?.events || item.events || [],
            };
          } catch {
            return { itemId: item.id, events: item.events || [] };
          }
        }),
      );
      const eventsByItemId = new Map(
        itemEventResponses.map((itemEvents) => [itemEvents.itemId, itemEvents.events]),
      );

      setOrder(
        nextOrder
          ? {
              ...nextOrder,
              items: (nextOrder.items || []).map((item) => ({
                ...item,
                events: eventsByItemId.get(item.id) || item.events || [],
              })),
            }
          : nextOrder,
      );
    } catch (error) {
      Swal.fire({ icon: "error", title: "Erro", html: getErrorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableEvents = async () => {
    setLoadingEventCatalog(true);
    setEventCatalogError("");

    try {
      const response = await api.get("/events", { params: { limit: 100 } });
      setAvailableEvents(getEventList(response));
    } catch (error) {
      setAvailableEvents([]);
      setEventCatalogError(
        getErrorMessage(error).replace(/<br\s*\/?\s*>/gi, ", "),
      );
    } finally {
      setLoadingEventCatalog(false);
    }
  };

  useEffect(() => {
    loadOrder();
    loadAvailableEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const updateItemEvents = (itemId, events) => {
    setOrder((currentOrder) => {
      if (!currentOrder) return currentOrder;

      return {
        ...currentOrder,
        items: (currentOrder.items || []).map((item) =>
          item.id === itemId ? { ...item, events } : item,
        ),
      };
    });
  };

  const refreshItemEvents = async (itemId) => {
    const response = await api.get(`/sales/${id}/items/${itemId}/events`);
    const events = response.data?.events || [];
    updateItemEvents(itemId, events);
    return events;
  };

  const finishEventAction = (itemId) => {
    setEventAction("");
    setEventDrafts((current) => ({ ...current, [itemId]: "" }));
  };

  const linkItemEvent = async (item) => {
    const eventId = eventDrafts[item.id];

    if (!eventId) {
      Swal.fire("Evento obrigatório", "Selecione um evento para vincular ao item.", "warning");
      return;
    }

    setEventAction(`link:${item.id}`);
    try {
      const response = await api.post(`/sales/${id}/items/${item.id}/events`, {
        eventId,
      });
      await refreshItemEvents(item.id);

      const reactivatedSelectionCount = response.data?.reactivatedSelectionCount || 0;
      await Swal.fire(
        "Vínculo atualizado",
        reactivatedSelectionCount
          ? `O evento foi reativado e ${reactivatedSelectionCount} seleção(ões) preservada(s) voltou(aram) a ficar ativa(s).`
          : "O evento foi vinculado ao item.",
        "success",
      );
    } catch (error) {
      Swal.fire({ icon: "error", title: "Erro", html: getErrorMessage(error) });
    } finally {
      finishEventAction(item.id);
    }
  };

  const showEventUnlinkSuccess = async (response) => {
    const deactivatedSelectionCount = response?.data?.deactivatedSelectionCount || 0;
    await Swal.fire(
      "Vínculo removido",
      deactivatedSelectionCount
        ? `O evento foi removido e ${deactivatedSelectionCount} seleção(ões) foram inativadas conforme o impacto confirmado.`
        : "O evento foi removido do item.",
      "success",
    );
  };

  const unlinkItemEvent = async (item, linkedEvent) => {
    const actionKey = `unlink:${item.id}:${linkedEvent.eventId}`;
    setEventAction(actionKey);

    try {
      const response = await api.delete(
        `/sales/${id}/items/${item.id}/events/${linkedEvent.eventId}`,
        { data: { confirmImpact: false } },
      );
      await refreshItemEvents(item.id);
      await showEventUnlinkSuccess(response);
    } catch (error) {
      if (!isUnlinkImpactConflict(error)) {
        Swal.fire({ icon: "error", title: "Erro", html: getErrorMessage(error) });
        setEventAction("");
        return;
      }

      const selectedPhotoCount = Number(error.response?.data?.selectedPhotoCount || 0);
      const confirmation = await Swal.fire({
        title: "Confirmar remoção do evento?",
        text: `Este vínculo possui ${selectedPhotoCount} foto(s) selecionada(s). A remoção irá inativá-las, preservando o histórico.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Remover e inativar seleções",
        cancelButtonText: "Cancelar",
      });

      if (!confirmation.isConfirmed) {
        setEventAction("");
        return;
      }

      try {
        const response = await api.delete(
          `/sales/${id}/items/${item.id}/events/${linkedEvent.eventId}`,
          { data: { confirmImpact: true } },
        );
        await refreshItemEvents(item.id);
        await showEventUnlinkSuccess(response);
      } catch (retryError) {
        Swal.fire({
          icon: "error",
          title: "Erro",
          html: getErrorMessage(retryError),
        });
      }
    } finally {
      setEventAction("");
    }
  };

  const transitionOrder = async (nextStatus) => {
    const confirmation = await Swal.fire({
      title: "Confirmar alteração de estado?",
      text: `O pedido passará para ${STATUS_LABELS[nextStatus] || nextStatus}.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirmar",
      cancelButtonText: "Cancelar",
    });
    if (!confirmation.isConfirmed) return;

    setLoading(true);
    try {
      const response = await api.patch(`/sales/${id}/status`, {
        commercialStatus: nextStatus,
      });
      setOrder(response.data.order);
      await Swal.fire("Atualizado", "Estado comercial atualizado com sucesso.", "success");
    } catch (error) {
      Swal.fire({ icon: "error", title: "Erro", html: getErrorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const reactivateOrder = async () => {
    setLoading(true);
    try {
      const previewResponse = await api.get(`/sales/${id}/reactivation-preview`);
      const preview = previewResponse.data.preview;
      const confirmation = await Swal.fire({
        title: "Reativar pedido?",
        text: `Serão preservados ${preview.itemCount} item(ns) e ${preview.linkedEventCount} evento(s) vinculado(s).`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Reativar",
        cancelButtonText: "Cancelar",
      });
      if (!confirmation.isConfirmed) return;

      const response = await api.post(`/sales/${id}/reactivate`);
      setOrder(response.data.order);
      await Swal.fire("Reativado", "Pedido reativado com sucesso.", "success");
    } catch (error) {
      Swal.fire({ icon: "error", title: "Erro", html: getErrorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const allSelectionsApproved = Boolean(
    order?.items?.length &&
      order.items.every((item) => item.selectionStatus === "SELECTION_APPROVED"),
  );
  const actions = order
    ? (NEXT_STATUS_ACTIONS[order.commercialStatus] || []).filter(
        (action) => !action.requiresApprovedSelections || allSelectionsApproved,
      )
    : [];
  const eventCatalogReady = !loadingEventCatalog && !eventCatalogError;

  const renderItemEvents = (item) => {
    const linkedEvents = item.events || [];
    const editable = isEventLinkEditable(order, item);
    const selectedEventId = eventDrafts[item.id] || "";
    const actionForItem = eventAction === `link:${item.id}`;

    return (
      <section className="item-eventos-panel" aria-label={`Eventos do item ${item.id}`}>
        <div className="item-eventos-header">
          <div className="item-eventos-heading">
            <span className="item-eventos-icon"><FiLink aria-hidden="true" /></span>
            <div>
              <span className="detail-kicker">Relações do item</span>
              <h4>Eventos vinculados</h4>
              <span>
                {linkedEvents.filter((event) => event.active).length} vínculo(s) ativo(s)
              </span>
            </div>
          </div>
          {item.selectionStatus && (
            <span className={`item-selecao-status ${getSelectionStatusTone(item.selectionStatus)}`}>
              {SELECTION_STATUS_LABELS[item.selectionStatus] || item.selectionStatus}
            </span>
          )}
        </div>

        {linkedEvents.length > 0 ? (
          <div className="item-eventos-lista">
            {linkedEvents.map((linkedEvent) => {
              const actionKey = `unlink:${item.id}:${linkedEvent.eventId}`;
              const actionInProgress = eventAction === actionKey;

              return (
                <div className="item-evento-vinculo" key={linkedEvent.id || linkedEvent.eventId}>
                  <div className="item-evento-info">
                    <span className="item-evento-icon"><FiCalendar aria-hidden="true" /></span>
                    <div>
                      <strong>{getEventName(linkedEvent, availableEvents)}</strong>
                      <span>
                        {linkedEvent.eventDate ? `${formatDate(linkedEvent.eventDate)} · ` : ""}
                        <span className={`item-evento-publication ${getPublicationStatusTone(linkedEvent.publicationStatus)}`}>
                          {formatPublicationStatus(linkedEvent.publicationStatus)}
                        </span>
                      </span>
                      {!linkedEvent.active && <small>Vínculo inativo preservado no histórico.</small>}
                    </div>
                  </div>
                  {linkedEvent.active && (
                    <button
                      type="button"
                      className="detail-button detail-button-danger"
                      onClick={() => unlinkItemEvent(item, linkedEvent)}
                      disabled={!editable || actionInProgress || loading}
                    >
                      <FiTrash2 aria-hidden="true" />
                      {actionInProgress ? "Removendo..." : "Remover vínculo"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="item-eventos-vazio">Nenhum evento vinculado a este item.</p>
        )}

        {editable ? (
          <div className="item-eventos-form">
            <label>
              Vincular ou reativar evento
              <select
                aria-label={`Vincular evento ao item ${item.id}`}
                value={selectedEventId}
                onChange={(event) =>
                  setEventDrafts((current) => ({
                    ...current,
                    [item.id]: event.target.value,
                  }))
                }
                disabled={!eventCatalogReady || actionForItem || loading}
              >
                <option value="">
                  {loadingEventCatalog ? "Carregando eventos..." : "Selecione um evento"}
                </option>
                {availableEvents.map((event) => {
                  const linkedEvent = linkedEvents.find((itemEvent) => itemEvent.eventId === event.id);
                  return (
                    <option
                      key={event.id}
                      value={event.id}
                      disabled={Boolean(linkedEvent?.active)}
                    >
                      {event.name}
                      {linkedEvent && !linkedEvent.active ? " · reativar vínculo" : ""}
                    </option>
                  );
                })}
              </select>
            </label>
            <button
              type="button"
              className="detail-button detail-button-primary"
              onClick={() => linkItemEvent(item)}
              disabled={!selectedEventId || actionForItem || loading || !eventCatalogReady}
            >
              <FiPlus aria-hidden="true" />
              {actionForItem ? "Salvando..." : "Vincular evento"}
            </button>
          </div>
        ) : (
          <p className="item-eventos-bloqueio">
            Os vínculos deste item estão preservados e não podem ser alterados no estado atual.
          </p>
        )}

        {eventCatalogError && editable && (
          <p className="item-eventos-erro" role="alert">
            {eventCatalogError}
          </p>
        )}
      </section>
    );
  };

  return (
    <div className="pagina-vendas-detalhe">
      <MenuLateral />

      <main className="venda-detalhe-conteudo">
        <header className="venda-detalhe-header">
          <button type="button" className="detail-back-button" onClick={() => navigate("/Vendas")}>
            <FiArrowLeft aria-hidden="true" />
            Voltar para vendas
          </button>
          <div className="venda-detalhe-title-row">
            <div>
              <span className="detail-kicker">Acompanhamento comercial</span>
              <h1>Detalhe do pedido</h1>
              <p>Consulte o estado atual, acompanhe os itens e mantenha os eventos vinculados ao pedido.</p>
            </div>
            {order && (
              <div className={`detail-header-status ${getCommercialStatusTone(order.commercialStatus)}`}>
                <span>Estado atual</span>
                <strong>{STATUS_LABELS[order.commercialStatus] || order.commercialStatus}</strong>
              </div>
            )}
          </div>
        </header>

        {loading && !order && <p role="status">Carregando pedido...</p>}

        {order && (
          <>
            <section className="venda-detalhe-overview" aria-label="Resumo do pedido">
              <div className="detail-buyer-card">
                <span className="detail-buyer-avatar"><FiUser aria-hidden="true" /></span>
                <div>
                  <span className="detail-card-label">Pessoa compradora</span>
                  <strong>{order.buyer?.name || "Pessoa indisponível"}</strong>
                  <small>Responsável por este pedido</small>
                </div>
              </div>
              <div className="detail-overview-stat">
                <span className="detail-card-label">Itens no pedido</span>
                <strong>{order.items?.length || 0}</strong>
                <small>Produto(s) registrado(s)</small>
              </div>
              <div className="detail-overview-stat">
                <span className="detail-card-label">Registrado em</span>
                <strong>{formatDate(order.createdAt)}</strong>
                <small>Data de criação</small>
              </div>
            </section>

            <section className="pedido-status-panel" aria-label="Ações do pedido">
              <div className="pedido-status-heading">
                <div className="pedido-status-icon"><FiCheckCircle aria-hidden="true" /></div>
                <div>
                  <span className="detail-kicker">Próximo movimento</span>
                  <h2>Ações do pedido</h2>
                </div>
              </div>
              <div className="pedido-status-content">
                <div className="pedido-status-copy">
                  {order.commercialStatus === "VENDA_CONCLUIDA" && (
                    <p>Venda concluída é somente leitura.</p>
                  )}
                  {order.commercialStatus === "PEDIDO_CANCELADO" && (
                    <p>O pedido está cancelado. Reative-o para continuar o acompanhamento operacional.</p>
                  )}
                  {order.commercialStatus === "PEDIDO_ORCADO" && (
                    <p>O aluno já pode montar e enviar a seleção de fotos. Após a aprovação e o fechamento externo, conclua a venda aqui.</p>
                  )}
                  {order.commercialStatus === "PEDIDO_REALIZADO" && (
                    <p>A seleção pode ser montada ou revisada. Após a aprovação e o fechamento externo, conclua a venda aqui.</p>
                  )}
                </div>
                <div className="pedido-status-actions">
                  {order.commercialStatus === "PEDIDO_CANCELADO" && (
                    <button type="button" className="detail-button detail-button-primary" onClick={reactivateOrder} disabled={loading}>
                      <FiRefreshCw aria-hidden="true" />
                      Reativar pedido
                    </button>
                  )}
                  {actions.map((action) => (
                    <button
                      type="button"
                      key={action.status}
                      className={`detail-button ${action.danger ? "detail-button-danger" : "detail-button-primary"}`}
                      onClick={() => transitionOrder(action.status)}
                      disabled={loading}
                    >
                      {action.danger ? <FiXCircle aria-hidden="true" /> : <FiCheckCircle aria-hidden="true" />}
                      {action.label}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            <section className="venda-produtos" aria-label="Itens do pedido">
              <div className="venda-produtos-heading">
                <div>
                  <span className="detail-kicker">Composição do pedido</span>
                  <h2>Itens e eventos vinculados</h2>
                  <p>Os vínculos determinam quais eventos ficam disponíveis para este pedido.</p>
                </div>
                <span className="detail-items-count">{order.items?.length || 0} {order.items?.length === 1 ? "item" : "itens"}</span>
              </div>
              {(order.items || []).map((item, index) => {
                const snapshot = item.productSnapshot || {};
                return (
                  <article key={item.id} className="venda-produto-card">
                    <header className="produto-card-header">
                      <div className="produto-card-title">
                        <span className="produto-card-icon"><FiPackage aria-hidden="true" /></span>
                        <div>
                          <span className="detail-kicker">Produto do pedido</span>
                          <h3>{snapshot.name || "Produto"}</h3>
                          <span className="produto-category">{formatProductCategory(snapshot.category)}</span>
                        </div>
                        {snapshot.description && <p>{snapshot.description}</p>}
                      </div>
                      <span className="produto-item-number">Item {index + 1}</span>
                    </header>
                    <div className="produto-status-row">
                      <div><span>Quantidade: {item.quantity}</span></div>
                      <div><span>Fotos obrigatórias: {item.requiredPhotoCount}</span></div>
                      <div><span>Eventos vinculados: {item.events?.filter((event) => event.active).length || 0}</span></div>
                    </div>
                    <button
                      type="button"
                      className="detail-button detail-button-selection"
                      onClick={() => navigate(
                        `/SelecaoFotos?${new URLSearchParams({
                          saleId: order.id,
                          itemId: item.id,
                        }).toString()}`,
                      )}
                    >
                      <FiImage aria-hidden="true" />
                      Abrir seleção
                    </button>
                    {renderItemEvents(item)}
                  </article>
                );
              })}
              {!order.items?.length && <p className="venda-empty">Nenhum item disponível neste pedido.</p>}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

export default VendaDetalhe;
